import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, X, Loader2, Plus, Trash2, Music, Mic, Map as MapIcon, Menu, ArrowUp, Code2, Copy } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { ImageUpload } from "@/components/ImageUpload";
import { PlanImage } from "@/threedvista/TdvExtras";
import { WindowEditor } from "@/threedvista/WindowEditor";

export function FileUpload({ value, name, onChange, accept, label, testid }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", f); const { data } = await api.post("/admin/upload", fd); onChange(data.url, f.name); }
    catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
    e.target.value = "";
  };
  if (value) return (
    <div className="flex items-center gap-2 rounded-xl border bg-white px-3 h-10 text-sm" data-testid={testid}>
      <span className="flex-1 truncate">{name || "Uploaded"}</span>
      <button type="button" onClick={() => onChange("", "")} className="w-7 h-7 rounded-full hover:bg-slate-100 grid place-items-center" data-testid={`${testid}-remove`}><X className="w-3.5 h-3.5" /></button>
    </div>
  );
  return (
    <>
      <button type="button" onClick={() => ref.current?.click()} disabled={busy} className="h-10 px-3 rounded-xl border-2 border-dashed text-sm text-slate-500 hover:text-slate-800 hover:border-slate-400 inline-flex items-center gap-2" data-testid={testid}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}{busy ? "Uploading…" : label}</button>
      <input ref={ref} type="file" accept={accept} className="hidden" onChange={pick} data-testid={`${testid}-input`} />
    </>
  );
}

const Row = ({ label, checked, onChange, testid }) => <label className="flex items-center justify-between gap-3 text-sm py-1">{label}<Switch checked={checked} onCheckedChange={onChange} data-testid={testid} /></label>;
const AUDIO = "audio/mpeg,audio/mp4,audio/x-m4a,audio/wav,audio/ogg,.mp3,.m4a,.wav,.ogg";

const KINDS = [["link", "Link button"], ["page", "Info page"], ["scene", "Jump to scene"], ["menu", "Dropdown menu"]];
const blank = (kind = "link") => ({ id: crypto.randomUUID(), label: "", kind, url: "", media_id: "", body: "", image_url: "", video_url: "", pdf_url: "", pdf_name: "", children: [] });

function MenuFields({ it, set, labels, tid }) {
  const f = (k) => (e) => set({ ...it, [k]: e.target.value });
  if (it.kind === "link") return <Input className="h-9 text-sm" value={it.url} maxLength={500} placeholder="https://…" onChange={f("url")} data-testid={`${tid}-url`} />;
  if (it.kind === "scene") return (
    <select value={it.media_id} onChange={f("media_id")} className="h-9 w-full rounded-lg border px-2 text-sm bg-white" data-testid={`${tid}-scene`}>
      <option value="">Choose a scene…</option>{Object.entries(labels).map(([id, l]) => <option key={id} value={id}>{l}</option>)}
    </select>
  );
  if (it.kind !== "page") return null;
  return (
    <div className="space-y-2">
      <Textarea rows={3} maxLength={3000} value={it.body} placeholder="Page text" onChange={f("body")} data-testid={`${tid}-body`} />
      <Input className="h-9 text-sm" value={it.video_url} maxLength={500} placeholder="Optional video: YouTube or Vimeo link" onChange={f("video_url")} data-testid={`${tid}-video`} />
      <div className="flex flex-wrap items-center gap-2"><ImageUpload value={it.image_url} onChange={(u) => set({ ...it, image_url: u })} testid={`${tid}-image`} />
        <FileUpload value={it.pdf_url} name={it.pdf_name} accept="application/pdf,.pdf" label="Upload PDF" onChange={(u, n) => set({ ...it, pdf_url: u, pdf_name: n })} testid={`${tid}-pdf`} /></div>
      <WindowEditor pin={{ title: it.label, body: it.body, image_url: it.image_url, video_url: it.video_url, pdf_url: it.pdf_url, pdf_name: it.pdf_name, win: it.win }} set={(win) => set({ ...it, win })} tid={tid} />
    </div>
  );
}

function RowHead({ it, set, del, up, tid, child }) {
  return (
      <div className="flex gap-2">
        <Input className="h-9 text-sm flex-1 min-w-[7rem]" value={it.label} maxLength={40} placeholder="Label, e.g. About" onChange={(e) => set({ ...it, label: e.target.value })} data-testid={`${tid}-label`} />
        <select value={it.kind} onChange={(e) => set({ ...it, kind: e.target.value })} className="h-9 rounded-lg border px-2 text-sm bg-white shrink-0" data-testid={`${tid}-kind`}>
          {KINDS.filter(([k]) => !child || k !== "menu").map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        {up && <button type="button" onClick={up} title="Move up" className="w-9 h-9 shrink-0 rounded-lg hover:bg-slate-100 grid place-items-center" data-testid={`${tid}-up`}><ArrowUp className="w-4 h-4" /></button>}
        <button type="button" onClick={del} className="w-9 h-9 shrink-0 rounded-lg text-rose-600 hover:bg-rose-50 grid place-items-center" data-testid={`${tid}-delete`}><Trash2 className="w-4 h-4" /></button>
      </div>
  );
}

function ChildRow({ it, set, del, labels, tid }) {
  return (
    <div className="rounded-2xl border p-3 space-y-2 bg-slate-50" data-testid={tid}>
      <RowHead it={it} set={set} del={del} tid={tid} child />
      <MenuFields it={it} set={set} labels={labels} tid={tid} />
    </div>
  );
}

function MenuRow({ it, set, del, up, labels, tid }) {
  return (
    <div className="rounded-2xl border p-3 space-y-2" data-testid={tid}>
      <RowHead it={it} set={set} del={del} up={up} tid={tid} />
      <MenuFields it={it} set={set} labels={labels} tid={tid} />
      {it.kind === "menu" && (
        <div className="pl-3 border-l-2 space-y-2">
          {(it.children || []).map((c, j) => <ChildRow key={c.id} it={c} labels={labels} tid={`${tid}-child-${j}`} set={(v) => set({ ...it, children: it.children.map((x) => (x.id === c.id ? v : x)) })} del={() => set({ ...it, children: it.children.filter((x) => x.id !== c.id) })} />)}
          {(it.children || []).length < 12 && <button type="button" onClick={() => set({ ...it, children: [...(it.children || []), blank()] })} className="text-xs font-semibold text-slate-600 inline-flex items-center gap-1" data-testid={`${tid}-add-child`}><Plus className="w-3.5 h-3.5" />Add item to this menu</button>}
        </div>
      )}
    </div>
  );
}

export function TdvMenuCard({ t, up, labels }) {
  const items = t.menu_items || [];
  const put = (v) => up({ menu_items: v });
  const move = (i) => { const n = [...items]; [n[i - 1], n[i]] = [n[i], n[i - 1]]; put(n); };
  return (
    <div className="rounded-3xl bg-white border p-5 space-y-3" data-testid="tdv-menu-card">
      <p className="font-semibold flex items-center gap-2"><Menu className="w-4 h-4" />Top menu bar</p>
      <p className="text-xs text-slate-500">Add link buttons, info pages, scene shortcuts or dropdown menus to this tour's top bar. On phones they sit under one Menu button. Items without a label are skipped.</p>
      {items.map((it, i) => <MenuRow key={it.id} it={it} labels={labels} tid={`tdv-menu-item-${i}`} set={(v) => put(items.map((x) => (x.id === it.id ? v : x)))} del={() => put(items.filter((x) => x.id !== it.id))} up={i > 0 ? () => move(i) : null} />)}
      {items.length < 8 && <button type="button" onClick={() => put([...items, blank()])} className="h-9 px-3 rounded-xl border-2 border-dashed text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-2" data-testid="tdv-menu-add"><Plus className="w-4 h-4" />Add menu item</button>}
    </div>
  );
}

const SIZES = [["wide", "Full width (16:9)"], ["tall", "Full width, 600px tall"], ["full", "Whole screen height"]];

function embedCode(id, size) {
  const src = `${window.location.origin}/3d/${id}`;
  const frame = `<iframe src="${src}" title="Virtual tour" allow="fullscreen; autoplay; xr-spatial-tracking; gyroscope; accelerometer" allowfullscreen loading="lazy" style="border:0;width:100%;height:${size === "wide" ? "100%" : size === "tall" ? "600px" : "100vh"};border-radius:12px"></iframe>`;
  return size === "wide" ? `<div style="position:relative;width:100%;aspect-ratio:16/9">${frame.replace("width:100%;height:100%", "position:absolute;inset:0;width:100%;height:100%")}</div>` : frame;
}

export function TdvEmbedCard({ t }) {
  const [size, setSize] = useState("wide");
  const code = embedCode(t.id, size);
  const copy = () => navigator.clipboard.writeText(code).then(() => toast.success("Embed code copied"), () => toast.error("Couldn't copy. Select the code and copy it."));
  return (
    <div className="rounded-3xl bg-white border p-5 space-y-3" data-testid="tdv-embed-card">
      <p className="font-semibold flex items-center gap-2"><Code2 className="w-4 h-4" />Embed on your website</p>
      <p className="text-xs text-slate-500">Paste this into your website (WordPress, Wix and Squarespace all have an “Embed” or “Custom HTML” block). Password protection still applies.</p>
      <select value={size} onChange={(e) => setSize(e.target.value)} className="h-9 w-full rounded-lg border px-2 text-sm bg-white" data-testid="tdv-embed-size">{SIZES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
      <textarea readOnly value={code} rows={5} onFocus={(e) => e.target.select()} className="w-full rounded-xl border bg-slate-50 p-3 font-mono text-[11px] leading-relaxed" data-testid="tdv-embed-code" />
      <button type="button" onClick={copy} className="h-9 px-4 rounded-full bg-slate-900 text-white text-sm font-semibold inline-flex items-center gap-2" data-testid="tdv-embed-copy"><Copy className="w-4 h-4" />Copy code</button>
      {!t.published && <p className="text-[11px] text-amber-700">This tour is unpublished, so the embed shows nothing until you publish it.</p>}
    </div>
  );
}

export function TdvSoundCard({ t, up, cur, label }) {
  const sa = t.scene_audio || {};
  return (
    <div className="rounded-3xl bg-white border p-5 space-y-3" data-testid="tdv-sound-card">
      <p className="font-semibold flex items-center gap-2"><Music className="w-4 h-4" />Music and voiceover</p>
      <div><p className="text-xs text-slate-500 mb-1">Background music (loops softly through the tour)</p>
        <FileUpload value={t.music_url} name={t.music_name || "Music track"} accept={AUDIO} label="Upload music" onChange={(u, n) => up({ music_url: u, music_name: n })} testid="tdv-music-upload" /></div>
      <div><p className="text-xs text-slate-500 mb-1 flex items-center gap-1"><Mic className="w-3.5 h-3.5" />Voiceover for {cur ? <b>{label}</b> : "the current scene"} (plays when visitors arrive)</p>
        {cur ? <FileUpload value={sa[cur.id]} name="Voiceover" accept={AUDIO} label="Upload voiceover" onChange={(u) => up({ scene_audio: { ...sa, [cur.id]: u } })} testid="tdv-voice-upload" /> : <p className="text-xs text-slate-400">Loading the tour…</p>}
        <p className="text-[11px] text-slate-400 mt-1">{Object.values(sa).filter(Boolean).length} scene(s) have a voiceover. Visitors get a mute button.</p></div>
    </div>
  );
}

export function TdvGuideCard({ t, up }) {
  return (
    <div className="rounded-3xl bg-white border p-5 space-y-1" data-testid="tdv-guide-card">
      <p className="font-semibold mb-1">Visitor guide</p>
      <Row label="Number our hotspots with Next button" checked={!!t.numbered} onChange={(v) => up({ numbered: v })} testid="tdv-toggle-numbered" />
      <p className="text-[11px] text-slate-400 -mt-1 mb-1">Numbers follow the order you added them.</p>
      <Row label="Show how-to-explore help card" checked={t.help_card !== false} onChange={(v) => up({ help_card: v })} testid="tdv-toggle-help" />
      <Row label="Show location map" checked={!!t.show_map} onChange={(v) => up({ show_map: v })} testid="tdv-toggle-map" />
      {t.show_map && <Input className="h-9 text-sm mt-1" value={t.address || ""} maxLength={300} placeholder="Address, e.g. 600 Frederick Road, Catonsville, MD" onChange={(e) => up({ address: e.target.value })} data-testid="tdv-address" />}
    </div>
  );
}

export function TdvPlanCard({ t, up, cur, labels }) {
  const plans = t.floorplans || [];
  const [sel, setSel] = useState(plans[0]?.id);
  const plan = plans.find((p) => p.id === sel) || plans[0];
  const pins = t.plan_pins || {};
  const add = (url) => { if (!url) return; const p = { id: crypto.randomUUID(), name: plans.length ? `Floor ${plans.length + 1}` : "Main floor", url }; up({ floorplans: [...plans, p] }); setSel(p.id); };
  const del = (p) => { up({ floorplans: plans.filter((x) => x.id !== p.id), plan_pins: Object.fromEntries(Object.entries(pins).filter(([, v]) => v.floor !== p.id)) }); setSel(plans[0]?.id); };
  return (
    <div className="rounded-3xl bg-white border p-5 space-y-3" data-testid="tdv-plan-card">
      <p className="font-semibold flex items-center gap-2"><MapIcon className="w-4 h-4" />Floor plans</p>
      <p className="text-xs text-slate-500">Upload a plan, then go to a scene and click the plan where it is. Visitors tap the dots to jump there.</p>
      {plans.map((p) => (
        <div key={p.id} className="flex items-center gap-2">
          <button type="button" onClick={() => setSel(p.id)} className={`w-3 h-3 rounded-full shrink-0 ${plan?.id === p.id ? "bg-slate-900" : "bg-slate-300"}`} data-testid={`tdv-plan-select-${p.id}`} />
          <Input className="h-9 text-sm" value={p.name} maxLength={40} onChange={(e) => up({ floorplans: plans.map((x) => (x.id === p.id ? { ...x, name: e.target.value } : x)) })} data-testid={`tdv-plan-name-${p.id}`} />
          <button type="button" onClick={() => del(p)} className="w-9 h-9 shrink-0 rounded-lg text-rose-600 hover:bg-rose-50 grid place-items-center" data-testid={`tdv-plan-delete-${p.id}`}><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      {plan && <div className="rounded-2xl bg-slate-100 p-2 grid place-items-center"><PlanImage plan={plan} pins={pins} labels={labels} current={cur?.id} onPlace={cur ? (pos) => up({ plan_pins: { ...pins, [cur.id]: pos } }) : null} /></div>}
      {plan && cur && <p className="text-[11px] text-slate-500">Click the plan to place <b>{labels[cur.id]}</b>.{pins[cur.id] && <button type="button" className="ml-2 text-rose-600 font-semibold" onClick={() => { const n = { ...pins }; delete n[cur.id]; up({ plan_pins: n }); }} data-testid="tdv-plan-unpin">Remove its dot</button>}</p>}
      {plans.length < 8 && <div className="flex items-center gap-2 text-xs text-slate-500"><Plus className="w-3.5 h-3.5" />Add a plan<ImageUpload value="" onChange={add} testid="tdv-plan-upload" /></div>}
    </div>
  );
}
